import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Text,
  Select as ChakraSelect,
} from '@chakra-ui/react';
import CreatableSelect from 'react-select/creatable';
import ReactSelect from 'react-select';
import { apiFetch } from './api';
import { reactSelectStyles } from './selectStyles';

const API_BASE = 'https://n441mm4tfl.execute-api.eu-west-2.amazonaws.com/prod';
const APIS_API = `${API_BASE}/listecrrepos`;

type OptionType = { value: string; label: string };
type ApiKeyItem = {
  username: string;
  allowedApis: string;
};
type ApiRule = { repo: string; subPath: string; methods: string[] };

const CopyableSingleValue = (props: any) => (
  <div
    style={{ userSelect: 'text', cursor: 'text', color: '#FFD600' }}
    onMouseDown={e => e.stopPropagation()}
    onTouchStart={e => e.stopPropagation()}
  >
    {props.data.label}
  </div>
);

export default function CreateApiKey() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [apis, setApis] = useState<string[]>([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [creating, setCreating] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');

  const [rules, setRules] = useState<ApiRule[]>([]);
  const [userApiMap, setUserApiMap] = useState<{ [user: string]: ApiRule[] }>({});
  const [allUsers, setAllUsers] = useState<string[]>([]);
  const [copyUser, setCopyUser] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const maxDateObj = new Date();
  maxDateObj.setFullYear(maxDateObj.getFullYear() + 10);
  const maxDate = maxDateObj.toISOString().split('T')[0];

  useEffect(() => {
    setReposLoading(true);
    apiFetch(APIS_API)
      .then(res => res.json())
      .then((data: string[]) => {
        setApis(data || []);
        setReposLoading(false);
      })
      .catch(() => {
        setApis([]);
        setReposLoading(false);
      });
  }, []);

  useEffect(() => {
    apiFetch(`${API_BASE}/listapikeys`)
      .then(res => res.json())
      .then((data: ApiKeyItem[]) => {
        const map: { [user: string]: ApiRule[] } = {};
        data.forEach(item => {
          try {
            const allowed = JSON.parse(item.allowedApis);
            map[item.username] = allowed.map((api: any) => {
              const parts = api.path.split('/').filter(Boolean);
              return {
                repo: parts[0] || '',
                subPath: parts.length > 1 ? parts.slice(1).join('/').replace(/\/\*$/, '') : '',
                methods: api.methods || ['*'],
              };
            });
          } catch {
            map[item.username] = [];
          }
        });
        setUserApiMap(map);
        setAllUsers(Object.keys(map));
      });
  }, []);

  useEffect(() => {
    if (copyUser && userApiMap[copyUser]) {
      setRules([...userApiMap[copyUser]]);
    }
  }, [copyUser, userApiMap]);

  function handleRuleChange(index: number, field: keyof ApiRule, value: any) {
    const updated = [...rules];
    (updated[index] as any)[field] = value;
    setRules(updated);
  }

  function addRule() {
    setRules([...rules, { repo: '', subPath: '', methods: [] }]);
  }

  function removeRule(index: number) {
    setRules(rules.filter((_, i) => i !== index));
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    setSuccessMsg('');

    if (expiryDate) {
      const exp = new Date(expiryDate);
      const now = new Date();
      if (exp < now) {
        setCreateError('Expiry date cannot be in the past');
        setCreating(false);
        return;
      }
      if (exp > maxDateObj) {
        setCreateError('Expiry date cannot be more than 10 years in the future');
        setCreating(false);
        return;
      }
    }

    const allowedApis = rules.flatMap(r => {
      const subPaths = r.subPath
        ? r.subPath.split(',').map(s => s.trim()).filter(Boolean)
        : ['*'];

      return subPaths.map(sp => {
        let path = `/${r.repo}`;
        if (sp !== '*' && sp !== '') path += `/${sp}`;
        else path += '/*';
        return { path, methods: r.methods.length > 0 ? r.methods : ['*'] };
      });
    });

    const payload = {
      username: username.trim(),
      email: email.trim(),
      allowedApis: allowedApis.length > 0 ? allowedApis : [{ path: '*', methods: ['*'] }],
      expiresAt: expiryDate || null,
    };

    try {
      const res = await apiFetch(`${API_BASE}/listapikeys`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        setCreateError(err.error || 'Failed to create API key');
      } else {
        setSuccessMsg('API key created and an email has been sent to the address provided.');
        setUsername('');
        setEmail('');
        setRules([]);
        setCopyUser('');
        setExpiryDate('');
      }
    } catch {
      setCreateError('Network error');
    }
    setCreating(false);
  }

  const methodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', '*'].map(m => ({
    value: m,
    label: m,
  }));

  return (
    <Box bg="brand.900" minH="100vh" color="brand.yellow" px={8} py={10}>
      <Box display="flex" alignItems="center" mb={6}>
        <Link to="/">
          <Button colorScheme="yellow" variant="outline" mr={6}>
            Back to API Keys
          </Button>
        </Link>
        <Heading size="lg" color="brand.yellowDark" mb={0}>
          Create API Key
        </Heading>
      </Box>

      <form onSubmit={handleCreate}>
        <FormControl isRequired mb={4}>
          <FormLabel>Username</FormLabel>
          <Input
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="Enter username"
            bg="brand.800"
            color="brand.yellow"
          />
        </FormControl>

        <FormControl isRequired mb={4}>
          <FormLabel>Email Address(es)</FormLabel>
          <Input
            type="text"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="e.g. alice@example.com, bob@example.com"
            bg="brand.800"
            color="brand.yellow"
          />
          <FormHelperText color="gray.400">Separate multiple addresses with commas. Each will receive their own one-time link.</FormHelperText>
        </FormControl>

        <FormControl mb={4}>
          <FormLabel>Expiry Date</FormLabel>
          <Input
            type="date"
            value={expiryDate}
            onChange={e => setExpiryDate(e.target.value)}
            min={today}
            max={maxDate}
            bg="brand.800"
            color="brand.yellow"
          />
          <FormHelperText color="gray.400">Defaults to 90 days if left blank.</FormHelperText>
        </FormControl>

        <FormControl mb={4}>
          <FormLabel>Allowed APIs</FormLabel>
          <FormHelperText color="gray.400" mb={3}>
            Select an API, optionally add subpaths, then choose the permitted HTTP methods.
          </FormHelperText>

          {/* Copy from existing user */}
          <Box mb={4} p={3} border="1px solid" borderColor="whiteAlpha.200" borderRadius="md">
            <Text fontSize="sm" mb={2} color="gray.400">Copy permissions from an existing user:</Text>
            <ChakraSelect
              value={copyUser || ''}
              onChange={e => setCopyUser(e.target.value.trim())}
              maxW={320}
              bg="brand.800"
              color="brand.yellow"
              size="sm"
            >
              <option value="">-- None --</option>
              {allUsers.map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </ChakraSelect>
          </Box>

          {rules.map((rule, index) => (
            <Box key={index} mb={3} p={3} border="1px solid #FFD600" borderRadius="md">
              <ReactSelect
                options={apis.map(api => ({ value: api, label: api }))}
                value={rule.repo ? { value: rule.repo, label: rule.repo } : null}
                onChange={opt => handleRuleChange(index, 'repo', opt ? opt.value : '')}
                placeholder={reposLoading ? 'Loading APIs…' : 'Select API'}
                isClearable
                isSearchable
                styles={reactSelectStyles}
                components={{ SingleValue: CopyableSingleValue }}
              />

              <Box mt={2}>
                <CreatableSelect
                  isMulti
                  placeholder="Subpaths — press Enter to add, leave blank for /*"
                  value={
                    rule.subPath
                      ? rule.subPath.split(',').map(sp => ({ value: sp.trim(), label: sp.trim() }))
                      : []
                  }
                  onChange={opts =>
                    handleRuleChange(index, 'subPath', (opts as OptionType[]).map(o => o.value).join(', '))
                  }
                  styles={reactSelectStyles}
                />
              </Box>

              <Box mt={2}>
                <ReactSelect
                  options={methodOptions}
                  value={rule.methods.map(m => ({ value: m, label: m }))}
                  isMulti
                  placeholder="Select methods"
                  onChange={opts => handleRuleChange(index, 'methods', (opts as OptionType[]).map(o => o.value))}
                  styles={reactSelectStyles}
                />
              </Box>

              <Button size="sm" mt={3} colorScheme="red" variant="outline" onClick={() => removeRule(index)}>
                Remove
              </Button>
            </Box>
          ))}

          <Button size="sm" mt={2} colorScheme="yellow" variant="outline" onClick={addRule}>
            + Add API
          </Button>
        </FormControl>

        <Button type="submit" colorScheme="yellow" isLoading={creating} mt={2}>
          Create API Key
        </Button>

        {createError && <Text color="red.400" mt={3}>{createError}</Text>}
        {successMsg && <Text color="green.400" mt={3}>{successMsg}</Text>}
      </form>
    </Box>
  );
}
